import React, { useState } from 'react';
import { ChevronLeft, LogOut } from 'lucide-react';
import MFASettings from './MFASettings';
import ThemeSettings from './ThemeSettings';
import NotificationSettings from './NotificationSettings';
import AccessibilitySettings from './components/AccessibilitySettings';
import PreferencesSection from './components/PreferencesSection';


import { API_URL } from './apiConfig';
import ReferralShare from './ReferralShare';

export default function SettingsView({
  onClose, profile, user, matches, messages, subscription,
  expandedSettings, setExpandedSettings,
  showSubscriptionManage, setShowSubscriptionManage,
  onLogout, onNeedsVerification,
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // AI Matching settings
  const [aiMatchSettings, setAiMatchSettings] = useState(null);
  const [aiMatchLoading, setAiMatchLoading] = useState(false);
  const [aiMatchSaving, setAiMatchSaving] = useState(false);

  const loadAiMatchSettings = async () => {
    if (aiMatchSettings !== null) return;
    setAiMatchLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/companion/matching/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      if (d.success) setAiMatchSettings(d.data);
    } catch (e) {}
    finally { setAiMatchLoading(false); }
  };

  const saveAiMatchSetting = async (patch) => {
    setAiMatchSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/companion/matching/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch)
      });
      const d = await res.json();
      if (d.success) setAiMatchSettings(prev => ({ ...prev, ...patch }));
    } catch (e) {}
    finally { setAiMatchSaving(false); }
  };
  const [changingPassword, setChangingPassword] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const handleChangePassword = async () => {
    if (passwordForm.new !== passwordForm.confirm) { alert('Passwords do not match'); return; }
    if (passwordForm.new.length < 8) { alert('Password must be at least 8 characters'); return; }
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/auth/change-password`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: passwordForm.current, newPassword: passwordForm.new })
      });
      const data = await res.json();
      if (data.success) { alert('Password updated successfully ✓'); setPasswordForm({ current: '', new: '', confirm: '' }); setShowCurrentPw(false); setShowNewPw(false); }
      else { alert(data.message || 'Failed to change password'); }
    } catch (err) { alert('Network error. Please try again.'); }
    setChangingPassword(false);
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true); setDeleteError('');
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/account/delete`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: deletePassword, reason: deleteReason })
      });
      const data = await res.json();
      if (data.success) { alert('Your account has been permanently deleted.'); onLogout(); }
      else { setDeleteError(data.message || 'Failed to delete account.'); }
    } catch (err) { setDeleteError('Network error. Please try again.'); }
    setDeleteLoading(false);
  };

  return (
          <div className="fade-in profile-scroll" style={{
            padding: '20px',
            maxHeight: 'calc(90vh - 130px)',
            overflowY: 'auto',
            background: 'var(--surface-dim)'
          }}>
            {/* Settings Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <button
                onClick={() => onClose()}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-primary)'
                }}
              >
                <ChevronLeft size={18} />
              </button>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  Settings
                </h3>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
                  App preferences & security
                </p>
              </div>
            </div>

            {/* ── Appearance ── */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setExpandedSettings({...expandedSettings, appearance: !expandedSettings.appearance})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: '0 0 12px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🎨</span>
                  <span>Appearance</span>
                </div>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSettings.appearance ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>
              {expandedSettings.appearance && (
                <ThemeSettings />
              )}
            </div>

            {/* ── Accessibility ── */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setExpandedSettings({...expandedSettings, accessibility: !expandedSettings.accessibility})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-primary)'
                }}
                aria-expanded={expandedSettings.accessibility}
                aria-label="Accessibility settings"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>♿</span>
                  <span>Accessibility</span>
                </div>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSettings.accessibility ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>
              {expandedSettings.accessibility && (
                <AccessibilitySettings />
              )}
            </div>

            {/* ── Tastes & preferences (food/music/sports) ── */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setExpandedSettings({...expandedSettings, preferences: !expandedSettings.preferences})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-primary)'
                }}
                aria-expanded={expandedSettings.preferences}
                aria-label="Tastes and preferences"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>🎯</span>
                  <span>Tastes</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: 400 }}>· optional</span>
                </div>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSettings.preferences ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>
              {expandedSettings.preferences && (
                <PreferencesSection />
              )}
            </div>

            {/* ── Security & Privacy ── */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setExpandedSettings({...expandedSettings, security: !expandedSettings.security})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: '0 0 12px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🔐</span>
                  <span>Security</span>
                </div>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSettings.security ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>
              {expandedSettings.security && (
                <div>
                  {/* Change Password */}
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ color: 'var(--text-primary)', fontSize: '14px', margin: '0 0 12px', fontWeight: 600 }}>Change Password</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showCurrentPw ? 'text' : 'password'}
                          placeholder="Current password"
                          value={passwordForm.current}
                          onChange={e => setPasswordForm(p => ({ ...p, current: e.target.value }))}
                          style={{
                            width: '100%', padding: '12px 44px 12px 14px', background: 'var(--bg-tertiary, #18181b)',
                            border: '1px solid var(--border, #27272a)', borderRadius: '10px', color: 'var(--text-primary)',
                            fontSize: '14px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box'
                          }}
                        />
                        <button
                          onClick={() => setShowCurrentPw(!showCurrentPw)}
                          style={{
                            position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                            background: 'none', border: 'none', color: 'var(--text-tertiary, #71717a)', cursor: 'pointer', padding: '4px'
                          }}
                        >
                          👁
                        </button>
                      </div>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showNewPw ? 'text' : 'password'}
                          placeholder="New password"
                          value={passwordForm.new}
                          onChange={e => setPasswordForm(p => ({ ...p, new: e.target.value }))}
                          style={{
                            width: '100%', padding: '12px 44px 12px 14px', background: 'var(--bg-tertiary, #18181b)',
                            border: '1px solid var(--border, #27272a)', borderRadius: '10px', color: 'var(--text-primary)',
                            fontSize: '14px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box'
                          }}
                        />
                        <button
                          onClick={() => setShowNewPw(!showNewPw)}
                          style={{
                            position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                            background: 'none', border: 'none', color: 'var(--text-tertiary, #71717a)', cursor: 'pointer', padding: '4px'
                          }}
                        >
                          👁
                        </button>
                      </div>
                      <input
                        type="password"
                        placeholder="Confirm new password"
                        value={passwordForm.confirm}
                        onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                        style={{
                          width: '100%', padding: '12px 14px', background: 'var(--bg-tertiary, #18181b)',
                          border: '1px solid var(--border, #27272a)', borderRadius: '10px', color: 'var(--text-primary)',
                          fontSize: '14px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box'
                        }}
                      />
                      <button
                        onClick={handleChangePassword}
                        disabled={changingPassword || !passwordForm.current || !passwordForm.new}
                        style={{
                          width: '100%', padding: '12px', border: 'none', borderRadius: '10px',
                          background: '#8b5cf6', color: '#fff', fontWeight: 700, fontSize: '14px',
                          cursor: 'pointer', fontFamily: 'inherit',
                          opacity: (changingPassword || !passwordForm.current || !passwordForm.new) ? 0.5 : 1
                        }}
                      >
                        {changingPassword ? 'Updating...' : 'Update Password'}
                      </button>
                    </div>
                  </div>

                  {/* Divider */}
                  <div style={{ borderTop: '1px solid var(--border, #27272a)', marginBottom: '16px' }} />

                  {/* Two-Factor Authentication */}
                  <MFASettings
                    apiUrl={API_URL}
                    token={localStorage.getItem('accessToken')}
                  />
                </div>
              )}
            </div>

            {/* ── Identity Verification ── */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setExpandedSettings({...expandedSettings, verification: !expandedSettings.verification})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: '0 0 12px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🛡️</span>
                  <span>Identity Verification</span>
                  {user?.isIdentityVerified && (
                    <span style={{
                      background: 'var(--success)',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: 'var(--radius-full)',
                      fontSize: '10px',
                      fontWeight: 600
                    }}>✓</span>
                  )}
                </div>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSettings.verification ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>
              {expandedSettings.verification && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '2px' }}>
                      {user?.isIdentityVerified ? 'Verified' : 'Not verified'}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {user?.isIdentityVerified
                        ? 'Your identity has been confirmed'
                        : 'Verify to unlock full features'}
                    </div>
                  </div>
                  {user?.isIdentityVerified ? (
                    <span style={{
                      background: 'var(--success)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: 'var(--radius-full)',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>✓ Verified</span>
                  ) : (
                    <button
                      onClick={() => { onClose(); onNeedsVerification(); }}
                      style={{
                        background: 'var(--primary)',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: 'var(--radius-full)',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Verify Now
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* ── Notifications ── */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setExpandedSettings({...expandedSettings, notifications: !expandedSettings.notifications})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: '0 0 12px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🔔</span>
                  <span>Notifications</span>
                </div>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSettings.notifications ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>
              {expandedSettings.notifications && (
                <NotificationSettings />
              )}
            </div>

            {/* ── Subscription ── */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setExpandedSettings({...expandedSettings, subscription: !expandedSettings.subscription})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: '0 0 12px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>👑</span>
                  <span>Subscription</span>
                  <span style={{
                    background: subscription?.status === 'active' ? 'var(--success)' :
                                subscription?.status === 'trial' ? '#f59e0b' :
                                'var(--text-muted)',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-full)',
                    fontSize: '10px',
                    fontWeight: 600
                  }}>
                    {subscription?.status === 'active' ? 'Active' :
                     subscription?.status === 'trial' ? 'Trial' :
                     subscription?.status === 'cancelled' ? 'Cancelled' : 'Expired'}
                  </span>
                </div>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSettings.subscription ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>
              {expandedSettings.subscription && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '2px' }}>
                      {subscription?.status === 'trial' ? 'Free Trial' :
                       subscription?.status === 'active' ? 'Narbodica Member' :
                       subscription?.status === 'cancelled' ? 'Cancelled' : 'Expired'}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {subscription?.isValid
                        ? `${subscription?.daysRemaining} day${subscription?.daysRemaining !== 1 ? 's' : ''} remaining`
                        : 'No active subscription'}
                    </div>
                  </div>
                  <button
                    onClick={() => { onClose(); setShowSubscriptionManage(true); }}
                    style={{
                      background: subscription?.status === 'trial' ? 'linear-gradient(135deg, #e11d48, #be123c)' : 'var(--surface)',
                      color: subscription?.status === 'trial' ? 'white' : 'var(--text-primary)',
                      border: subscription?.status === 'trial' ? 'none' : '1px solid var(--border)',
                      padding: '8px 16px',
                      borderRadius: 'var(--radius-full)',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {subscription?.status === 'trial' ? 'Subscribe' : 'Manage'}
                  </button>
                </div>
              )}
            </div>

            {/* ── AI Matching ── */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => {
                  setExpandedSettings({...expandedSettings, aiMatching: !expandedSettings.aiMatching});
                  if (!expandedSettings.aiMatching) loadAiMatchSettings();
                }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', background: 'none', border: 'none',
                  padding: '0 0 12px 0', cursor: 'pointer', fontSize: '14px',
                  fontWeight: 600, color: 'var(--text-primary)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🤖</span>
                  <span>AI Matching</span>
                  {aiMatchSettings?.enabled && (
                    <span style={{ fontSize: '10px', background: 'rgba(124,106,247,0.15)', color: '#a78bfa', padding: '2px 7px', borderRadius: '12px', fontWeight: 600 }}>ON</span>
                  )}
                </div>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSettings.aiMatching ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
              </button>
              {expandedSettings.aiMatching && (
                <div>
                  {aiMatchLoading ? (
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '8px 0' }}>Loading...</div>
                  ) : (
                    <div>
                      {/* Explainer */}
                      <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '16px', padding: '12px', background: 'var(--bg-secondary, rgba(255,255,255,0.04))', borderRadius: '10px' }}>
                        <strong style={{ color: 'var(--text-primary)', display: 'block', marginBottom: '6px' }}>How it works</strong>
                        When enabled, Maia reviews profiles on your behalf and surfaces matches she thinks you'd genuinely connect with — based on everything she knows about you. You always decide whether to see their profile or pass.
                        <br /><br />
                        This is opt-in, private, and you can turn it off at any time.
                      </div>

                      {/* Enable AI Matching toggle */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '2px' }}>Enable AI matching</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Let Maia surface matches for you</div>
                        </div>
                        <button
                          type="button"
                          disabled={aiMatchSaving}
                          onClick={() => saveAiMatchSetting({ enabled: !aiMatchSettings?.enabled })}
                          style={{
                            width: '44px', height: '24px', borderRadius: '12px', border: 'none',
                            cursor: aiMatchSaving ? 'not-allowed' : 'pointer',
                            background: aiMatchSettings?.enabled ? '#7c6af7' : 'var(--border, #3a3a3a)',
                            position: 'relative', transition: 'background 0.2s', flexShrink: 0
                          }}
                        >
                          <span style={{
                            position: 'absolute', top: '3px',
                            left: aiMatchSettings?.enabled ? '23px' : '3px',
                            width: '18px', height: '18px', borderRadius: '50%',
                            background: '#fff', transition: 'left 0.2s', display: 'block'
                          }} />
                        </button>
                      </div>

                      {/* AI-to-AI evaluation toggle — only visible when enabled */}
                      {aiMatchSettings?.enabled && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'var(--bg-secondary, rgba(255,255,255,0.04))', borderRadius: '10px' }}>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '2px' }}>AI-to-AI compatibility check</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                              When both people have AI matching on, Maia does a deeper compatibility evaluation before surfacing a match. Takes 24–48h but improves quality significantly.
                            </div>
                          </div>
                          <button
                            type="button"
                            disabled={aiMatchSaving}
                            onClick={() => saveAiMatchSetting({ negotiationEnabled: !aiMatchSettings?.negotiationEnabled })}
                            style={{
                              width: '44px', height: '24px', borderRadius: '12px', border: 'none',
                              cursor: aiMatchSaving ? 'not-allowed' : 'pointer',
                              background: aiMatchSettings?.negotiationEnabled ? '#7c6af7' : 'var(--border, #3a3a3a)',
                              position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: '12px'
                            }}
                          >
                            <span style={{
                              position: 'absolute', top: '3px',
                              left: aiMatchSettings?.negotiationEnabled ? '23px' : '3px',
                              width: '18px', height: '18px', borderRadius: '50%',
                              background: '#fff', transition: 'left 0.2s', display: 'block'
                            }} />
                          </button>
                        </div>
                      )}

                      {aiMatchSettings?.enabled && (
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '10px', lineHeight: 1.5 }}>
                          AI matching uses information from your Maia profile and conversations. You can review what Maia knows about you in the Maia app. Turning this off stops new suggestions immediately.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── About ── */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setExpandedSettings({...expandedSettings, about: !expandedSettings.about})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: '0 0 12px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>ℹ️</span>
                  <span>About Spark</span>
                </div>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSettings.about ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>
              {expandedSettings.about && (
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Spark</strong> — Dating done right
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginBottom: '16px' }}>
                    Version 1.0 • A Narbodica App
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{
                      color: 'var(--primary)', textDecoration: 'none', fontSize: '13px', fontWeight: 500
                    }}>
                      📄 Privacy Policy
                    </a>
                    <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{
                      color: 'var(--primary)', textDecoration: 'none', fontSize: '13px', fontWeight: 500
                    }}>
                      📋 Terms of Service
                    </a>
                    <a href="mailto:hello@narbodica.com.au" style={{
                      color: 'var(--primary)', textDecoration: 'none', fontSize: '13px', fontWeight: 500
                    }}>
                      ✉️ Contact Support
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* ── Delete Account ── */}
            <div className="profile-section" style={{ marginBottom: '16px', borderColor: 'var(--danger, #ef4444)' }}>
              <button
                type="button"
                onClick={() => setExpandedSettings({...expandedSettings, deleteAccount: !expandedSettings.deleteAccount})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: '0 0 12px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--danger, #ef4444)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>⚠️</span>
                  <span>Delete Account</span>
                </div>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSettings.deleteAccount ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>
              {expandedSettings.deleteAccount && (
                <div>
                  <div style={{
                    padding: '14px',
                    borderRadius: '12px',
                    background: 'rgba(239, 68, 68, 0.08)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    marginBottom: '16px'
                  }}>
                    <p style={{ margin: '0 0 8px', fontWeight: 600, color: 'var(--danger, #ef4444)', fontSize: '14px' }}>
                      This action is permanent and cannot be undone.
                    </p>
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      Deleting your account will permanently remove your profile, photos, matches, messages, dates, and subscription. You will not be able to recover any data.
                    </p>
                  </div>

                  {!showDeleteConfirm ? (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'transparent',
                        color: 'var(--danger, #ef4444)',
                        border: '1px solid var(--danger, #ef4444)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      I want to delete my account
                    </button>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                          Enter your password to confirm
                        </label>
                        <input
                          type="password"
                          value={deletePassword}
                          onChange={e => setDeletePassword(e.target.value)}
                          placeholder="Your password"
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--border)',
                            background: 'var(--surface)',
                            color: 'var(--text-primary)',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                          Why are you leaving? (optional)
                        </label>
                        <textarea
                          value={deleteReason}
                          onChange={e => setDeleteReason(e.target.value)}
                          placeholder="Help us improve..."
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--border)',
                            background: 'var(--surface)',
                            color: 'var(--text-primary)',
                            fontSize: '13px',
                            minHeight: '60px',
                            resize: 'vertical',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      {deleteError && (
                        <div style={{
                          padding: '10px 14px',
                          borderRadius: 'var(--radius-md)',
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          fontSize: '13px',
                          color: 'var(--danger, #ef4444)'
                        }}>
                          {deleteError}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); setDeleteReason(''); setDeleteError(''); }}
                          style={{
                            flex: 1,
                            padding: '12px',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: 600,
                            background: 'var(--surface)',
                            color: 'var(--text-primary)'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteAccount}
                          disabled={!deletePassword || deleteLoading}
                          style={{
                            flex: 1,
                            padding: '12px',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            cursor: !deletePassword || deleteLoading ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            fontWeight: 600,
                            background: !deletePassword ? '#94a3b8' : '#dc2626',
                            color: 'white',
                            opacity: deleteLoading ? 0.6 : 1
                          }}
                        >
                          {deleteLoading ? 'Deleting...' : 'Permanently Delete'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Gifts & Referrals ── */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', cursor: 'pointer' }}
                onClick={() => setShowReferral(r => !r)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px' }}>🎁</span>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>Gifts & Referrals</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Invite friends · Leaderboard</div>
                  </div>
                </div>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{showReferral ? '▲' : '▼'}</span>
              </div>
              {showReferral && (
                <div style={{ paddingTop: '8px' }}>
                  <ReferralShare user={user} subscription={subscription} />
                </div>
              )}
            </div>

            {/* ── Logout ── */}
            <button
              onClick={onLogout}
              style={{
                width: '100%',
                padding: '14px',
                background: 'var(--surface)',
                color: 'var(--danger)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginTop: '8px',
                transition: 'all 0.2s'
              }}
            >
              <LogOut size={16} />
              Log Out
            </button>

            <div style={{ height: '20px' }} />
          </div>
  );
}
